import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { THEME } from '../../utils/theme';

const CommandPalette = ({ onClose, commands }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter(cmd =>
      cmd.label.toLowerCase().includes(q) ||
      cmd.keywords?.some(k => k.toLowerCase().includes(q)) ||
      cmd.category?.toLowerCase().includes(q)
    );
  }, [query, commands]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups = {};
    filteredCommands.forEach(cmd => {
      const cat = cmd.category || 'Actions';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(cmd);
    });
    return groups;
  }, [filteredCommands]);

  // Flatten for keyboard navigation
  const flatCommands = useMemo(() => filteredCommands, [filteredCommands]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatCommands.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatCommands[selectedIndex]) {
      e.preventDefault();
      flatCommands[selectedIndex].action();
      onClose();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const categoryIcons = {
    'Navigation': '🧭',
    'Actions': '⚡',
    'Discover': '🔍',
    'Squad': '👥',
    'View': '👁️',
    'Settings': '⚙️',
  };

  let currentIdx = 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)', paddingTop: '15vh' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -20 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
        style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '20px', width: '95%', maxWidth: '560px', maxHeight: '60vh', display: 'flex', flexDirection: 'column', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow }}
        onClick={e => e.stopPropagation()}
      >
        {/* Search Input */}
        <div style={{ padding: '16px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '18px', color: THEME.textMuted }}>⌘</span>
          <input
            ref={inputRef}
            type="text"
            placeholder="Type a command or search..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              flex: 1, background: 'transparent', border: 'none', color: THEME.text,
              fontSize: '16px', outline: 'none'
            }}
          />
          <kbd style={{ background: THEME.bg, padding: '4px 8px', borderRadius: '6px', fontSize: '11px', color: THEME.textMuted, border: `1px solid ${THEME.border}` }}>ESC</kbd>
        </div>

        {/* Command List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {flatCommands.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: THEME.textMuted }}>
              No commands found for "{query}"
            </div>
          ) : (
            Object.entries(groupedCommands).map(([category, cmds]) => (
              <div key={category} style={{ marginBottom: '12px' }}>
                <div style={{ padding: '8px 12px', color: THEME.textMuted, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>{categoryIcons[category] || '📁'}</span>
                  {category}
                </div>
                {cmds.map(cmd => {
                  const idx = currentIdx++;
                  const isSelected = idx === selectedIndex;
                  return (
                    <button
                      key={cmd.id}
                      onClick={() => { cmd.action(); onClose(); }}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '10px 12px', borderRadius: '8px', border: 'none',
                        background: isSelected ? `${THEME.accent}20` : 'transparent',
                        color: THEME.text, fontSize: '14px', cursor: 'pointer', textAlign: 'left',
                        transition: 'background 0.1s'
                      }}
                    >
                      <span style={{ fontSize: '16px', width: '24px', textAlign: 'center' }}>{cmd.icon}</span>
                      <span style={{ flex: 1, fontWeight: isSelected ? '600' : '400' }}>{cmd.label}</span>
                      {cmd.shortcut && (
                        <kbd style={{ background: THEME.bg, padding: '2px 6px', borderRadius: '4px', fontSize: '10px', color: THEME.textMuted, border: `1px solid ${THEME.border}` }}>{cmd.shortcut}</kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${THEME.border}`, display: 'flex', gap: '16px', justifyContent: 'center' }}>
          <span style={{ fontSize: '11px', color: THEME.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <kbd style={{ background: THEME.bg, padding: '2px 6px', borderRadius: '4px', border: `1px solid ${THEME.border}` }}>↑↓</kbd> navigate
          </span>
          <span style={{ fontSize: '11px', color: THEME.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <kbd style={{ background: THEME.bg, padding: '2px 6px', borderRadius: '4px', border: `1px solid ${THEME.border}` }}>↵</kbd> select
          </span>
          <span style={{ fontSize: '11px', color: THEME.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <kbd style={{ background: THEME.bg, padding: '2px 6px', borderRadius: '4px', border: `1px solid ${THEME.border}` }}>esc</kbd> close
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CommandPalette;
